import * as React from 'react';
import { AuthPrompt } from '@bw-ui/basket/AuthPrompt';
import { BookingSummary } from '@bw-ui/basket/BookingSummary';
import { BookingSummarySkeleton } from '@bw-ui/basket/BookingSummarySkeleton';
import { DiscountCodeInput } from '@bw-ui/basket/DiscountCodeInput';
import type { DiscountLine } from '@bw-ui/basket/BookingSummary';

export interface CheckoutAsideProps {
  isLoggedIn: boolean;
  isLoadingPreview: boolean;
  /**
   * Account-credit balance still in flight. Held on the skeleton until it
   * lands, otherwise the summary shows the pre-credit amount due for a moment
   * and then drops — the user sees a price change they didn't cause.
   */
  isLoadingBalance?: boolean;
  invoicePreview: unknown;
  noPreviewAvailable: boolean;
  summary: {
    subtotal: number;
    tax: number;
    total: number;
    discountLines: DiscountLine[];
    accountCredits?: number;
    amountDue?: number;
  };
  onProceed: () => void;
  onProceedLoading: boolean;
  cancellationPolicyMinutes?: number;
  canCancelBooking?: boolean;
  error?: string;
  onApplyDiscount: (code: string) => Promise<void>;
  appliedCode?: string;
  discountError?: string;
  signInHref: string;
  createAccountHref?: string;
}

export function CheckoutAside({
  isLoggedIn,
  isLoadingPreview,
  isLoadingBalance = false,
  invoicePreview,
  noPreviewAvailable,
  summary: { subtotal, tax, total, discountLines, accountCredits, amountDue },
  onProceed,
  onProceedLoading,
  cancellationPolicyMinutes,
  canCancelBooking,
  error,
  onApplyDiscount,
  appliedCode,
  discountError,
  signInHref,
  createAccountHref,
}: CheckoutAsideProps) {
  if (!isLoggedIn) {
    return (
      <aside className="tw:flex tw:flex-col tw:gap-6">
        <AuthPrompt
          signInHref={signInHref}
          createAccountHref={createAccountHref}
        />
      </aside>
    );
  }

  return (
    <aside className="tw:flex tw:flex-col tw:gap-6">
      {(isLoadingPreview && !invoicePreview) || isLoadingBalance ? (
        <BookingSummarySkeleton />
      ) : (
        <BookingSummary
          subtotal={noPreviewAvailable ? 0 : subtotal}
          tax={noPreviewAvailable ? 0 : tax}
          total={noPreviewAvailable ? 0 : total}
          accountCredits={noPreviewAvailable ? 0 : (accountCredits ?? 0)}
          amountDue={noPreviewAvailable ? 0 : (amountDue ?? total)}
          discountLines={discountLines}
          onProceed={onProceed}
          onProceedLoading={onProceedLoading}
          cancellationPolicyMinutes={cancellationPolicyMinutes}
          canCancelBooking={canCancelBooking}
          error={error}
        >
          <DiscountCodeInput
            onApply={onApplyDiscount}
            appliedCode={appliedCode}
            error={discountError}
          />
        </BookingSummary>
      )}
    </aside>
  );
}
